import type { FetchArgs } from '@reduxjs/toolkit/query/react';
import { idbGet, idbPut, STORE_RESPONSES } from './offlineDb';

/**
 * Copia local del catálogo para abrir la caja sin red (modo offline, F1).
 *
 * Cada GET de la lista de abajo que responde bien se guarda en IndexedDB; si después
 * el servidor no contesta, baseApi devuelve la última copia en vez del error. Las
 * pantallas no se enteran: ven los datos de siempre, y el aviso de "Sin conexión"
 * dice que puede no estar al día.
 *
 * Solo corre en la caja offline (utils/serviceWorker.ts). Las órdenes NO están en la
 * lista a propósito: una orden vieja servida como vigente es peor que un error, y la
 * operación con órdenes sin red es la fase F2 (docs/modo-offline.md).
 */

/**
 * Rutas (después de /api) que se guardan. Es el catálogo que necesita el mapa de
 * mesas para dibujarse y para tomar una orden, más lo que la app pide al arrancar
 * (/auth/me: sin él AuthProvider no tiene usuario y el arranque no pasa).
 */
const CACHEABLE_PREFIXES = [
  '/auth/me',
  '/areas',
  '/tables',
  '/categories',
  '/products', // incluye /products/variant-groups, que la caja necesita para poner precios
  '/daily-menus/active',
  '/daily-menus/anchor-product-id',
  '/productioncenter/active',
  '/productioncenter/exists',
  '/kitchen/notes',
  '/settings/',
  '/paymentmethods',
  '/restaurants/settings',
  '/facturacion/config',
  '/users/waiters',
  '/stripe/subscription-status',
  '/organization/seat-limit',
];

export const isCacheablePath = (path: string): boolean =>
  CACHEABLE_PREFIXES.some((prefix) =>
    prefix.endsWith('/')
      ? path.startsWith(prefix)
      : path === prefix || path.startsWith(`${prefix}/`) || path.startsWith(`${prefix}?`));

/**
 * Parte de la llave que sale de la petición: ruta + parámetros, o null si la
 * petición no se guarda (no es GET o no es catálogo).
 */
export const cacheableRequestKey = (args: string | FetchArgs): string | null => {
  const url = typeof args === 'string' ? args : args.url;
  const method = typeof args === 'string' ? 'GET' : (args.method ?? 'GET').toUpperCase();
  if (method !== 'GET' || !isCacheablePath(url)) return null;

  const params = typeof args === 'string' ? undefined : args.params;
  if (!params || Object.keys(params).length === 0) return url;
  const sorted = Object.keys(params).sort().map((k) => [k, String(params[k])]);
  return `${url}#${JSON.stringify(sorted)}`;
};

/**
 * Llave completa. Va por usuario Y restaurante: el catálogo cambia al cambiar de
 * restaurante (RestaurantSwitcher), y /auth/me de una cajera no puede servirle a otra.
 */
export const offlineCacheKey = (userId: string, restaurantId: string, requestKey: string) =>
  `${userId}|${restaurantId}|${requestKey}`;

// ── Guardado (IndexedDB, ver offlineDb) ──────────────────────────────────────

interface StoredResponse {
  key: string;
  data: unknown;
  savedAt: string;
}

/** Nunca lanza: una copia que no se guardó no puede romper la petición que sí salió bien. */
export async function saveOfflineResponse(key: string, data: unknown): Promise<void> {
  try {
    const record: StoredResponse = { key, data, savedAt: new Date().toISOString() };
    await idbPut(STORE_RESPONSES, record);
  } catch (err) {
    console.warn('No se pudo guardar la copia offline:', key, err);
  }
}

/** undefined = no hay copia (o IndexedDB no está disponible). */
export async function loadOfflineResponse(key: string): Promise<unknown | undefined> {
  try {
    const stored = await idbGet<StoredResponse>(STORE_RESPONSES, key);
    return stored?.data;
  } catch {
    return undefined;
  }
}
